import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, effect, forwardRef, inject, input, model } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ZagIcon } from '../icon/icon';

@Component({
  selector: 'zag-number-input',
  imports: [ZagIcon],
  templateUrl: './number-input.html',
  styleUrl: './number-input.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NumberInput),
      multi: true,
    },
  ],
})
export class NumberInput implements ControlValueAccessor {
  private readonly cdr = inject(ChangeDetectorRef);

  readonly placeholder = input<string>('');
  readonly disabled = input<boolean>(false);
  readonly readonly = input<boolean>(false);
  readonly id = input<string | undefined>(undefined);
  readonly name = input<string | undefined>(undefined);
  readonly autocomplete = input<string | undefined>(undefined);
  readonly required = input<boolean>(false);
  readonly min = input<number | undefined>(undefined);
  readonly max = input<number | undefined>(undefined);
  readonly step = input<number>(1);
  readonly clearable = input<boolean>(true);
  readonly showControls = input<boolean>(true);
  readonly suffix = input<string | undefined>(undefined);
  /**
   * When true, the input's right corners are flattened so it can sit flush
   * against a trailing addon whose container owns the outer border radius.
   */
  readonly flushRight = input<boolean>(false);
  /** Horizontal text alignment of the field value. Defaults to center (existing behavior). */
  readonly textAlign = input<'left' | 'center' | 'right'>('center');
  readonly value = model<number | null>(null);

  protected readonly showClearButton = computed(() => this.clearable() && this.value() !== null && !this.disabled() && !this.readonly());

  protected readonly canDecrement = computed(() => {
    if (this.disabled() || this.readonly()) {
      return false;
    }

    const value = this.value();
    const min = this.min();
    if (value === null || min === undefined) {
      return true;
    }

    return value > min;
  });

  protected readonly canIncrement = computed(() => {
    if (this.disabled() || this.readonly()) {
      return false;
    }

    const value = this.value();
    const max = this.max();
    if (value === null || max === undefined) {
      return true;
    }

    return value < max;
  });

  protected readonly displayValue = computed(() => {
    const value = this.value();
    return value === null ? '' : String(value);
  });

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: number | null) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {};

  constructor() {
    effect(() => {
      if (this.disabled()) {
        this.cdr.markForCheck();
      }
    });
  }

  writeValue(value: number | null): void {
    this.value.set(value === null ? null : this.clamp(value));
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(_isDisabled: boolean): void {
    this.cdr.markForCheck();
  }

  protected onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const rawValue = target.value;
    if (rawValue === '') {
      this.updateValue(null);
      return;
    }

    const nextValue = Number(rawValue);
    if (Number.isNaN(nextValue)) {
      return;
    }

    this.updateValue(this.clamp(nextValue));
  }

  protected onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.increment();
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.decrement();
    }
  }

  protected decrement(): void {
    if (!this.canDecrement()) {
      return;
    }

    const current = this.value() ?? 0;
    this.updateValue(this.clamp(this.applyStepDelta(current, -this.step())));
  }

  protected increment(): void {
    if (!this.canIncrement()) {
      return;
    }

    const current = this.value() ?? 0;
    this.updateValue(this.clamp(this.applyStepDelta(current, this.step())));
  }

  protected onBlur(): void {
    this.onTouched();
  }

  protected clear(event: Event): void {
    event.stopPropagation();
    this.updateValue(null);
    this.onTouched();
  }

  private updateValue(value: number | null): void {
    this.value.set(value);
    this.onChange(value);
  }

  private clamp(value: number): number {
    const min = this.min();
    const max = this.max();
    let nextValue = value;

    if (min !== undefined && nextValue < min) {
      nextValue = min;
    }

    if (max !== undefined && nextValue > max) {
      nextValue = max;
    }

    return nextValue;
  }

  private applyStepDelta(current: number, delta: number): number {
    const precision = Math.max(this.getDecimalPlaces(current), this.getDecimalPlaces(delta));

    if (precision === 0) {
      return current + delta;
    }

    const factor = 10 ** precision;
    return Math.round((current + delta) * factor) / factor;
  }

  private getDecimalPlaces(value: number): number {
    const normalized = value.toString().toLowerCase();
    if (normalized.includes('e-')) {
      const [, exponent = '0'] = normalized.split('e-');
      const base = normalized.split('e-')[0] ?? normalized;
      const fractional = base.split('.')[1]?.length ?? 0;
      return Number(exponent) + fractional;
    }

    return normalized.split('.')[1]?.length ?? 0;
  }
}
